import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import NotificationGrid from 'flarum/forum/components/NotificationGrid';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostControls from 'flarum/forum/utils/PostControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import Badge from 'flarum/common/components/Badge';
import User from 'flarum/common/models/User';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';

import ClubsIndexPage from './components/ClubsIndexPage';
import ClubSettingsModal from './components/ClubSettingsModal';
import ClubLeaderModal from './components/ClubLeaderModal';
import ClubMentionedNotification from './components/ClubMentionedNotification';
import ClubJoinRequestedNotification from './components/ClubJoinRequestedNotification';
import ClubMention from './mentionables/ClubMention';
import ClubPinnedPost from './components/ClubPinnedPost';
import decorateClubMentions from './utils/decorateClubMentions';
import { apiUrl, canLeadClub, clubForDiscussion, clubsOfUser, coverStyle, toSummary, updateClubSummary } from '../common/clubs';

app.initializers.add('happydigital-clubs', () => {
  app.routes['happydigital-clubs.index'] = { path: '/kulupler', component: ClubsIndexPage };
  app.notificationComponents.clubMentioned = ClubMentionedNotification;
  app.notificationComponents.clubJoinRequested = ClubJoinRequestedNotification;

  // flarum/mentions is optional; register only when its @ format exists.
  // Must run before the composer first builds its suggestion list.
  const atFormat = app.mentionFormats && app.mentionFormats.get('@');

  if (atFormat) atFormat.extend(ClubMention);

  // A join-request notification links here with ?kulup=istekler: open the
  // requests panel straight away.
  // `this.discussion` is only assigned once the post stream has loaded, so use
  // the discussion show() was called with.
  extend(DiscussionPage.prototype, 'show', function (returned, discussion) {
    if (m.route.param('kulup') !== 'istekler') return;

    const club = clubForDiscussion(discussion);

    if (club && canLeadClub(club)) {
      setTimeout(() => app.modal.show(ClubLeaderModal, { club }), 0);
    }
  });

  extend(NotificationGrid.prototype, 'notificationTypes', function (items) {
    items.add('clubJoinRequested', {
      name: 'clubJoinRequested',
      icon: 'fas fa-user-plus',
      label: app.translator.trans('happydigital-clubs.forum.settings.notify_club_join_requested_label'),
    });

    items.add('clubMentioned', {
      name: 'clubMentioned',
      icon: 'fas fa-bullhorn',
      label: app.translator.trans('happydigital-clubs.forum.settings.notify_club_mentioned_label'),
    });
  });

  extend(IndexPage.prototype, 'navItems', function (items) {
    items.add(
      'happydigital-clubs',
      LinkButton.component(
        { href: app.route('happydigital-clubs.index'), icon: 'fas fa-users' },
        app.translator.trans('happydigital-clubs.forum.nav.clubs')
      ),
      85
    );
  });

  // One badge per club, always with the club's own icon and colour; the role
  // (president / vice / member) is in the badge's tooltip.
  extend(User.prototype, 'badges', function (items) {
    clubsOfUser(this).forEach(({ club, role }) => {
      const key = role ? `badge.${role}` : 'badge.member';

      items.add(
        `happydigital-club-${club.id}`,
        Badge.component({
          type: `club-${role || 'member'}`,
          icon: club.icon || 'fas fa-users',
          color: club.color || undefined,
          label: extractText(app.translator.trans(`happydigital-clubs.forum.${key}`, { club: club.name })),
        }),
        role ? 100 : 90
      );
    });
  });

  // The club's cover behind the discussion header, and its name as a label.
  extend(DiscussionHero.prototype, 'view', function (vdom) {
    const club = clubForDiscussion(this.attrs.discussion);

    if (!club || !vdom || !vdom.attrs) return;

    vdom.attrs.className = classList(vdom.attrs.className || vdom.attrs.class, 'ClubDiscussionHero', {
      'ClubDiscussionHero--cover': !!club.coverUrl,
    });
    vdom.attrs.class = null;

    const style = coverStyle(club);

    if (style) vdom.attrs.style = Object.assign({}, vdom.attrs.style || {}, style);
  });

  extend(DiscussionHero.prototype, 'items', function (items) {
    const club = clubForDiscussion(this.attrs.discussion);

    if (!club) return;

    // Byobu lists every recipient in the header; on a club that is the whole
    // member list, which crowds the cover. Members stay one click away in
    // byobu's own "edit recipients" modal.
    if (items.has('recipients')) items.remove('recipients');

    items.add(
      'happydigital-club',
      <span className="ClubDiscussionHero-club">
        {icon(club.icon || 'fas fa-users')} {app.translator.trans('happydigital-clubs.forum.discussion.label')}
      </span>,
      110
    );

    if (club.description) {
      items.add('happydigital-club-description', <p className="ClubDiscussionHero-description">{club.description}</p>, -10);
    }
  });

  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    const club = clubForDiscussion(discussion);
    const canManage = app.forum.attribute('happydigitalClubsCanManage');

    if (canManage && (club || discussion.attribute('isPrivateDiscussion'))) {
      items.add(
        'happydigital-club-settings',
        Button.component(
          { icon: 'fas fa-users-cog', onclick: () => app.modal.show(ClubSettingsModal, { discussion }) },
          app.translator.trans(club ? 'happydigital-clubs.forum.controls.settings' : 'happydigital-clubs.forum.controls.make_club')
        )
      );
    }

    if (club && canLeadClub(club)) {
      items.add(
        'happydigital-club-leader',
        Button.component(
          { icon: 'fas fa-user-check', onclick: () => app.modal.show(ClubLeaderModal, { club }) },
          app.translator.trans('happydigital-clubs.forum.controls.manage')
        )
      );
    }
  });

  // ── Byobu recipient modal on a club ─────────────────────────────────────
  // Without the forum-wide "more than two recipients" permission, byobu's
  // modal assumes a one-to-one conversation: picking a user drops every other
  // selected user. Club leaders only hold that right inside their own club
  // (ClubDiscussionPolicy), so the modal wiped the whole member list whenever
  // they added someone. While the modal is open on a club the actor runs,
  // tell it the right is there; restore the real value when it closes.
  const byobuModals = flarum.extensions['fof-byobu'] && flarum.extensions['fof-byobu'].modals;
  const AddRecipientModal = byobuModals && byobuModals.AddRecipientModal;

  if (AddRecipientModal) {
    extend(AddRecipientModal.prototype, 'oninit', function () {
      const club = this.attrs.discussion && clubForDiscussion(this.attrs.discussion);
      const real = app.forum.attribute('canAddMoreThanTwoUserRecipients');

      if (!club || !canLeadClub(club) || real) return;

      this.clubRestoreMoreThanTwo = real;
      app.forum.pushAttributes({ canAddMoreThanTwoUserRecipients: true });
    });

    extend(AddRecipientModal.prototype, 'onremove', function () {
      if (this.clubRestoreMoreThanTwo === undefined) return;

      app.forum.pushAttributes({ canAddMoreThanTwoUserRecipients: this.clubRestoreMoreThanTwo });
      this.clubRestoreMoreThanTwo = undefined;
    });
  }

  // ── Pinned message ──────────────────────────────────────────────────────
  // Shown inside the stream column, above the posts. The page lays out its
  // nav and stream as grid areas, so a separate sibling would break the grid.
  extend(DiscussionPage.prototype, 'mainContent', function (items) {
    const club = clubForDiscussion(this.discussion);

    if (!club || !club.pinnedPostId || !items.has('poststream')) return;

    const stream = items.get('poststream');

    items.setContent(
      'poststream',
      <div className={stream.attrs.className}>
        {[<ClubPinnedPost postId={club.pinnedPostId} stream={this.stream} />].concat(stream.children || [])}
      </div>
    );
  });

  extend(PostControls, 'moderationControls', function (items, post) {
    const club = post.discussion && clubForDiscussion(post.discussion());

    if (!club || !canLeadClub(club) || post.contentType() !== 'comment' || post.isHidden()) return;

    const pinned = String(club.pinnedPostId) === post.id();

    items.add(
      'happydigital-club-pin',
      Button.component(
        { icon: 'fas fa-thumbtack', onclick: () => togglePin(club, pinned ? null : post) },
        app.translator.trans(pinned ? 'happydigital-clubs.forum.pin.unpin' : 'happydigital-clubs.forum.pin.pin')
      )
    );
  });

  // Effective @shortname mentions (flagged by the server) render as chips.
  const decorate = function () {
    const post = this.attrs.post;

    if (!post.attribute('happydigitalClubMention') || !this.element) return;

    const club = post.discussion && clubForDiscussion(post.discussion());

    decorateClubMentions(this.element.querySelector('.Post-body'), club);
  };

  extend(CommentPost.prototype, 'oncreate', decorate);
  extend(CommentPost.prototype, 'onupdate', decorate);

  // The pinned post keeps a club-coloured accent in the stream, so it is easy
  // to spot after "Go to message", even near the end of the discussion where
  // the page cannot scroll it to the top.
  extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
    const post = this.attrs.post;
    const club = post.discussion && clubForDiscussion(post.discussion());

    if (!club || String(club.pinnedPostId) !== post.id()) return;

    attrs.className = classList(attrs.className, 'ClubPinnedPost-target');

    if (club.color) attrs.style = Object.assign({}, attrs.style || {}, { '--club-color': club.color });
  });

  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    const club = post.discussion && clubForDiscussion(post.discussion());

    if (!club || String(club.pinnedPostId) !== post.id()) return;

    items.add(
      'happydigital-club-pinned',
      <span className="ClubPinnedPost-marker">
        {icon('fas fa-thumbtack')} {app.translator.trans('happydigital-clubs.forum.pin.marker')}
      </span>,
      -10
    );
  });
});

/**
 * Pin a post as the club's message, or unpin with `post = null`.
 */
function togglePin(club, post) {
  app
    .request({
      method: post ? 'POST' : 'DELETE',
      url: apiUrl('/' + club.id + '/pin'),
      body: post ? { data: { attributes: { postId: post.id() } } } : undefined,
    })
    .then((response) => {
      updateClubSummary(response.data.id, toSummary(response.data));
      m.redraw();
    })
    .catch(() => {});
}
